import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { MessageSquare, Mail, Phone, Building2, Clock, Eye, Trash2, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusOptions = [
  { value: 'new', label: 'New', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'contacted', label: 'Contacted', color: 'bg-sky-100 text-sky-700' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-amber-100 text-amber-700' },
  { value: 'closed', label: 'Closed', color: 'bg-stone-100 text-stone-700' },
];

const LeadInquiries = () => {
  const { language } = useLanguage();
  const [inquiries, setInquiries] = useState([]);
  const [filteredInquiries, setFilteredInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [updateData, setUpdateData] = useState({ status: '', notes: '' });

  useEffect(() => {
    fetchInquiries();
  }, []);

  useEffect(() => {
    filterInquiries();
  }, [inquiries, searchTerm, filterStatus]);

  const fetchInquiries = async () => {
    try {
      const response = await axios.get(`${API}/inquiries`);
      setInquiries(response.data);
    } catch (error) {
      toast.error(language === 'en' ? 'Failed to fetch inquiries' : 'Gagal mengambil pertanyaan');
    } finally {
      setLoading(false);
    }
  };

  const filterInquiries = () => {
    let filtered = [...inquiries];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(i => 
        i.name.toLowerCase().includes(term) ||
        i.email.toLowerCase().includes(term) ||
        i.company?.toLowerCase().includes(term) ||
        i.message.toLowerCase().includes(term)
      );
    }
    
    if (filterStatus !== 'all') {
      filtered = filtered.filter(i => i.status === filterStatus);
    }
    
    setFilteredInquiries(filtered);
  };

  const openDetailDialog = (inquiry) => {
    setSelectedInquiry(inquiry);
    setUpdateData({ status: inquiry.status, notes: inquiry.notes || '' });
    setDialogOpen(true);
  };

  const handleUpdate = async () => {
    try {
      await axios.put(`${API}/inquiries/${selectedInquiry.id}`, updateData);
      toast.success(language === 'en' ? 'Inquiry updated!' : 'Pertanyaan diperbarui!');
      setDialogOpen(false);
      fetchInquiries();
    } catch (error) {
      toast.error(language === 'en' ? 'Failed to update' : 'Gagal memperbarui');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(language === 'en' ? 'Delete this inquiry?' : 'Hapus pertanyaan ini?')) return;
    
    try {
      await axios.delete(`${API}/inquiries/${id}`);
      toast.success(language === 'en' ? 'Inquiry deleted!' : 'Pertanyaan dihapus!');
      fetchInquiries();
    } catch (error) {
      toast.error(language === 'en' ? 'Failed to delete' : 'Gagal menghapus');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'en' ? 'en-US' : 'id-ID', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const found = statusOptions.find(s => s.value === status);
    return found ? found.color : 'bg-stone-100 text-stone-700';
  };

  if (loading) {
    return (
      <AdminLayout title={language === 'en' ? 'Lead Inquiries' : 'Pertanyaan Lead'}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
        </div>
      </AdminLayout>
    );
  }

  const newCount = inquiries.filter(i => i.status === 'new').length;

  return (
    <AdminLayout title={language === 'en' ? 'Lead Inquiries' : 'Pertanyaan Lead'}>
      <div className="space-y-6" data-testid="lead-inquiries-page">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {statusOptions.map(status => {
            const count = inquiries.filter(i => i.status === status.value).length;
            return (
              <Card key={status.value} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilterStatus(status.value)}>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-stone-900">{count}</p>
                  <p className="text-sm text-stone-500">{status.label}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <Input
                  placeholder={language === 'en' ? 'Search inquiries...' : 'Cari pertanyaan...'}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-inquiries"
                />
              </div>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-48" data-testid="filter-status">
                  <SelectValue placeholder={language === 'en' ? 'All Status' : 'Semua Status'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{language === 'en' ? 'All Status' : 'Semua Status'}</SelectItem>
                  {statusOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Inquiries List */}
        <Card data-testid="inquiries-list-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-sky-500" />
              {language === 'en' ? 'Inquiries' : 'Pertanyaan'} ({filteredInquiries.length})
              {newCount > 0 && (
                <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full ml-2">
                  {newCount} {language === 'en' ? 'new' : 'baru'}
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {filteredInquiries.length > 0 ? (
              <div className="space-y-4">
                {filteredInquiries.map((inquiry, index) => (
                  <div 
                    key={inquiry.id}
                    className="p-4 bg-stone-50 rounded-lg hover:bg-stone-100 transition-colors"
                    data-testid={`inquiry-item-${index}`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="font-medium text-stone-900">{inquiry.name}</h4>
                          <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(inquiry.status)}`}>
                            {inquiry.status}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-4 text-sm text-stone-500">
                          <span className="flex items-center gap-1">
                            <Mail className="w-4 h-4" />
                            {inquiry.email}
                          </span>
                          {inquiry.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-4 h-4" />
                              {inquiry.phone}
                            </span>
                          )}
                          {inquiry.company && (
                            <span className="flex items-center gap-1">
                              <Building2 className="w-4 h-4" />
                              {inquiry.company}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-stone-600 mt-2 line-clamp-2">{inquiry.message}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-stone-400 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(inquiry.created_at)}
                        </span>
                        <Button variant="ghost" size="sm" onClick={() => openDetailDialog(inquiry)} data-testid={`view-inquiry-${index}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(inquiry.id)} className="text-red-600 hover:text-red-700" data-testid={`delete-inquiry-${index}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-stone-500">
                {language === 'en' ? 'No inquiries found' : 'Tidak ada pertanyaan ditemukan'}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Detail Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="inquiry-detail-dialog">
          <DialogHeader>
            <DialogTitle>
              {language === 'en' ? 'Inquiry Details' : 'Detail Pertanyaan'}
            </DialogTitle>
          </DialogHeader>
          
          {selectedInquiry && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-stone-500">{language === 'en' ? 'Name' : 'Nama'}</Label>
                  <p className="font-medium">{selectedInquiry.name}</p>
                </div>
                <div>
                  <Label className="text-stone-500">Email</Label>
                  <p className="font-medium">{selectedInquiry.email}</p>
                </div>
                {selectedInquiry.phone && (
                  <div>
                    <Label className="text-stone-500">{language === 'en' ? 'Phone' : 'Telepon'}</Label>
                    <p className="font-medium">{selectedInquiry.phone}</p>
                  </div>
                )}
                {selectedInquiry.company && (
                  <div>
                    <Label className="text-stone-500">{language === 'en' ? 'Company' : 'Perusahaan'}</Label>
                    <p className="font-medium">{selectedInquiry.company}</p>
                  </div>
                )}
              </div>

              <div>
                <Label className="text-stone-500">{language === 'en' ? 'Message' : 'Pesan'}</Label>
                <p className="p-3 bg-stone-50 rounded-lg mt-1">{selectedInquiry.message}</p>
              </div>

              <div>
                <Label className="text-stone-500">{language === 'en' ? 'Received' : 'Diterima'}</Label>
                <p className="font-medium">{formatDate(selectedInquiry.created_at)}</p>
              </div>

              <hr />

              <div>
                <Label>{language === 'en' ? 'Update Status' : 'Perbarui Status'}</Label>
                <Select value={updateData.status} onValueChange={(v) => setUpdateData({...updateData, status: v})}>
                  <SelectTrigger className="mt-1" data-testid="update-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>{language === 'en' ? 'Internal Notes' : 'Catatan Internal'}</Label>
                <Textarea
                  value={updateData.notes}
                  onChange={(e) => setUpdateData({...updateData, notes: e.target.value})}
                  placeholder={language === 'en' ? 'Add notes about this inquiry...' : 'Tambah catatan tentang pertanyaan ini...'}
                  className="mt-1"
                  rows={3}
                  data-testid="inquiry-notes"
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  {language === 'en' ? 'Close' : 'Tutup'}
                </Button>
                <Button onClick={handleUpdate} className="bg-emerald-600 hover:bg-emerald-700" data-testid="update-inquiry-btn">
                  {language === 'en' ? 'Update' : 'Perbarui'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default LeadInquiries;
